// Shared keyword-based defect categorization for the Defect-wise Report
// (ProductList) and the GIS map filter (MultiMap).
//
// The two screens were drifting: ProductList used keyword substring rules and
// bucketed every leaf label correctly, while MultiMap used exact-label matching
// against IBI umbrella names that the YOLO model never emits as leaves — so
// every defect fell through to a single "Other" group under Road Safety.
// Routing both screens through this util keeps them in lockstep.

// Rules are checked in order; first match wins. Keywords are matched as
// case-insensitive substrings against the (severity-suffix-stripped) label.
// The trailing "IBI umbrella labels" block exists for orgs whose data still
// emits umbrella names directly (e.g. BangaloreOrg).
export const DEFECT_CATEGORIZATION_RULES = [
  // ── Roadway Surface ──
  { cat: 'pavement', subKey: 'pavement_condition', label: 'Pavement Condition',
    kw: ['pothole','cracking','crack','rutting','rut','stripping','delamination','pavement damage','unsealed','settlement','water stagnation','pavement joint','patching'] },
  { cat: 'pavement', subKey: 'shoulder_condition', label: 'Shoulder Condition',
    kw: ['shoulder','edge drop','unevenness','rain cut'] },
  // ── Asset Management ──
  { cat: 'assets', subKey: 'signages', label: 'Signages',
    kw: ['sign board','sign structure','signage'] },
  { cat: 'assets', subKey: 'change_detection', label: 'Change Detection',
    kw: ['street light','highway light'] },
  // Road Safety variant runs BEFORE the assets crash_barriers rule so labels
  // like "Barriers - Faded Painting Guard Rails" match the roadSafety bucket
  // (per canonical taxonomy) instead of falling into assets via 'guard rail'.
  { cat: 'roadSafety', subKey: 'crash_barriers_rs', label: 'Crash Barriers & Guard Rails',
    kw: ['faded painting'] },
  { cat: 'assets', subKey: 'crash_barriers', label: 'Crash Barriers & Guard Rails',
    kw: ['damaged crash barrier','guardrail','guard rail','mbcb','pgr'] },
  { cat: 'assets', subKey: 'kerb_median', label: 'Kerb & Median',
    kw: ['damaged kerb'] },
  { cat: 'assets', subKey: 'drainage', label: 'Drainage',
    kw: ['drain cover'] },
  { cat: 'assets', subKey: 'footpath', label: 'Footpath',
    kw: ['footpath','paver block','paver'] },
  { cat: 'assets', subKey: 'safety_devices', label: 'Safety Devices',
    kw: ['blinker','attenuator','delineator','anti-glare','anti glare'] },
  { cat: 'assets', subKey: 'road_studs_markers', label: 'Road Studs & Markers',
    kw: ['road stud','hazard marker','stud'] },
  { cat: 'assets', subKey: 'bus_bay', label: 'Bus Bay & Truck Lay-By',
    kw: ['bus bay','truck lay'] },
  { cat: 'assets', subKey: 'plantation', label: 'Plantation / Landscaping',
    kw: ['missing plant','irregular gap','deteriorated plant','damaged plant','overgrowth'] },
  { cat: 'assets', subKey: 'unauthorized_assets', label: 'Unauthorized Activity',
    kw: ['unauthorized hoarding','advertisement board','holding advertisement'] },
  // ── Road Safety ──
  { cat: 'roadSafety', subKey: 'rumble_strips', label: 'Rumble Strips',
    kw: ['rumble strip'] },
  { cat: 'roadSafety', subKey: 'pavement_markings', label: 'Pavement Markings',
    kw: ['pavement marking','faded pavement'] },
  { cat: 'roadSafety', subKey: 'work_zone', label: 'Work Zone Safety',
    kw: ['work zone','diversion','barricad','inappropriate diversion'] },
  { cat: 'roadSafety', subKey: 'kerb_median_rs', label: 'Kerb & Median',
    kw: ['faded kerb','kerb painting','median separator'] },
  { cat: 'roadSafety', subKey: 'drainage_rs', label: 'Drainage',
    kw: ['manhole'] },
  { cat: 'roadSafety', subKey: 'unauthorized_rs', label: 'Unauthorized Activity',
    kw: ['unauthorized median','unauthorized signboard','encroachment','illegal parking','general encroachment'] },
  { cat: 'roadSafety', subKey: 'vegetation', label: 'Vegetation & Cleanliness',
    kw: ['vegetation','plantation growth','reduced visibility','reduced sight','cleanliness','litter','debris'] },
  // ── IBI umbrella labels (per IBI Guideline.docx) ──
  // Roadway Surface
  { cat: 'pavement', subKey: 'pavement_defects', label: 'Pavement Defects',
    kw: ['pavement defects'] },
  { cat: 'pavement', subKey: 'road_surface', label: 'Road Surface',
    kw: ['road surface'] },
  { cat: 'pavement', subKey: 'road_structure', label: 'Road Structure',
    kw: ['road structure','flyover','underpass'] },
  // Asset Management
  { cat: 'assets', subKey: 'lighting', label: 'Lighting',
    kw: ['flood light','high mast'] },
  { cat: 'assets', subKey: 'median_assets', label: 'Median',
    kw: ['median'] },
  { cat: 'assets', subKey: 'pedestrian_signal', label: 'Pedestrian Signal',
    kw: ['pedestrian signal'] },
  // Road Safety — pedestrian infrastructure + behaviour + obstructions
  { cat: 'roadSafety', subKey: 'pedestrian_infra', label: 'Pedestrian Infrastructure',
    kw: ['pedestrian crossing','foot over bridge','fob','kerb ramp'] },
  { cat: 'roadSafety', subKey: 'traffic_calming', label: 'Traffic Calming',
    kw: ['speed hump','refuge island'] },
  { cat: 'roadSafety', subKey: 'natural', label: 'Natural Hazards',
    kw: ['natural','overhanging branches'] },
  { cat: 'roadSafety', subKey: 'infrastructure', label: 'Infrastructure',
    kw: ['infrastructure','electric pole','traffic kiosk'] },
  { cat: 'roadSafety', subKey: 'traffic_behaviour', label: 'Traffic Behaviour',
    kw: ['traffic behaviour','wrong side'] },
];

// Resolve a defect label to its top tab ('pavement' | 'assets' | 'roadSafety')
// using the same first-match-wins iteration as categorizeDefectsByKeyword.
// Returns null when no rule matches.
export function categorizeDefectName(name) {
  const lo = (name || '').toLowerCase().replace(/\s+(high|medium|low)$/i, '').trim();
  for (const rule of DEFECT_CATEGORIZATION_RULES) {
    if (rule.kw.some(k => lo.includes(k))) return rule.cat;
  }
  return null;
}

// Flat-list variant: { pavement: [items], assets: [items], roadSafety: [items] }.
// Callers that just need tab routing (FinalReport, ProductList chart tabs) use
// this; callers that need subcategory rollups (MultiMap, Settings) keep using
// categorizeDefectsByKeyword.
export function categorizeFlat(items, getName) {
  const result = { pavement: [], assets: [], roadSafety: [] };
  items.forEach(item => {
    const cat = categorizeDefectName(getName(item));
    if (cat) result[cat].push(item);
  });
  return result;
}

// Group items into { pavement, assets, roadSafety } > [{ subKey, subLabel, items }].
// `getName` extracts the label string used for keyword matching, so callers can
// pass plain strings (ProductList) or checkbox objects (MultiMap) and get back
// groups containing the original item shape.
// Items that match no rule are dropped — the rule set above is intentionally
// broad enough to cover every label our pipeline emits today.
export function categorizeDefectsByKeyword(items, getName) {
  const assigned = { pavement: {}, assets: {}, roadSafety: {} };
  items.forEach(item => {
    const name = getName(item) || '';
    const lo = name.toLowerCase().replace(/\s+(high|medium|low)$/i, '').trim();
    for (const rule of DEFECT_CATEGORIZATION_RULES) {
      if (rule.kw.some(k => lo.includes(k))) {
        if (!assigned[rule.cat][rule.subKey]) {
          assigned[rule.cat][rule.subKey] = { subKey: rule.subKey, subLabel: rule.label, items: [] };
        }
        assigned[rule.cat][rule.subKey].items.push(item);
        return;
      }
    }
  });
  return {
    pavement: Object.values(assigned.pavement),
    assets: Object.values(assigned.assets),
    roadSafety: Object.values(assigned.roadSafety),
  };
}
